import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import { CharacterName } from "../GameObjects/Character";

export enum PRIORITY {
    NEVER = -1,
    EVENTUALLY = 0,
    LATER = 3,
    SOON = 5,
    NEXT = 9,
    IMMEDIATELY = 10,
}
enum STATUS {
    NOT_LOADED,
    LOADING,
    LOADED,
}
interface IAudioFile {
    priority: PRIORITY;
    status: STATUS;
    filename: string;
    character: CharacterName;
    culture: Culture;
    alternate: boolean;
    category: string;
}

interface IAudioPreloadRequest {
    character: CharacterName;
    culture: Culture;
    alternate: boolean;
    priority: PRIORITY;
}

interface IAudioPriorityUpdateRequest {
    character?: CharacterName;
    culture?: Culture;
    alternate?: boolean;
    priority: PRIORITY;
}

const CATEGORY = Constants.AUDIO_CATEGORY_SOLO;
const MAX_CONCURRENT_LOADS = 1;
const A_SUFFIX: string = "_A";
const B_SUFFIX: string = "_B";
const MIN_PRIORITY_TO_LOAD = 0;
const AUTO_UNLOAD = true;

export default class AudioPreloadManager {
    public static debug: boolean = false;
    private static _paused: boolean = true;
    public static set paused(pValue: boolean) { AudioPreloadManager._paused = pValue; }
    private static readonly queue: IAudioFile[] = new Array<IAudioFile>();

    public static update() {
        if (AudioPreloadManager._paused) {
            return;
        }
        else {
            const loading = AudioPreloadManager.queue.filter((f) => f.status === STATUS.LOADING);
            if (loading.length >= MAX_CONCURRENT_LOADS) {
                return;
            }
            else {
                AudioPreloadManager.LoadNextFile();
            }
        }
    }

    public static Enqueue(pRequest: IAudioPreloadRequest): void {
        const filename = AudioPreloadManager.getFilename(pRequest.culture, pRequest.character, pRequest.alternate);

        if (AudioPreloadManager.GetFileFromQueue(filename) !== undefined) {
            if (this.debug) {
                console.error("[AudioPreloadManager] File " + filename + " is already queued for loading.");
            }
        }
        else {
            const newFile: IAudioFile = {
                alternate: pRequest.alternate,
                culture: pRequest.culture,
                character: pRequest.character,
                priority: pRequest.priority,
                status: STATUS.NOT_LOADED,
                filename,
                category: CATEGORY,
            };
            AudioPreloadManager.queue.push(newFile);
        }
    }

    public static UpdatePriority(pRequest: IAudioPriorityUpdateRequest): void {
        if (pRequest.character === undefined &&
            pRequest.culture === undefined &&
            pRequest.alternate === undefined
        ) {
            console.error("[AudioPreloadManager] IAudioPriorityUpdateRequest must contain " +
              "at least one of: character, culture, or alternate");
            return;
        }
        else {
            const numFiles = AudioPreloadManager.queue.length;
            const files = AudioPreloadManager.queue.filter((f) => {
                if (pRequest.culture !== undefined) {
                    if (f.culture !== pRequest.culture) {
                        return false;
                    }
                }
                if (pRequest.character !== undefined) {
                    if (f.character !== pRequest.character) {
                        return false;
                    }
                }
                if (pRequest.alternate !== undefined) {
                    if (f.alternate !== pRequest.alternate) {
                        return false;
                    }
                }
                return true;
            });
            if (files.length === 0) {
                console.warn("[AudioPreloadManager] No files matching filter: ", pRequest);
            }
            else {
                files.forEach((f) => {
                    if (f.priority !== pRequest.priority) {
                        if (this.debug) {
                            console.log("[AudioPreloadManager] Updating priority for " +
                                f.filename + " from " + f.priority + " to " + pRequest.priority);
                        }
                        f.priority = pRequest.priority;
                    }
                });
            }
        }
    }

    public static getFilename(pCulture: Culture, pCharacter: CharacterName, pAlternate: boolean): string {
        const suffix = pAlternate ? B_SUFFIX : A_SUFFIX;
        return "solo_" + pCharacter.toString() + "_" + pCulture.toString() + suffix;
    }

    public static isFileLoaded(pFilename: string) {
        const file = AudioPreloadManager.GetFileFromQueue(pFilename);
        if (file === undefined) {
            return false;
        }
        else {
            return file.status === STATUS.LOADED;
        }
    }

    private static LoadNextFile(): void {
        AudioPreloadManager.queue.sort((a: IAudioFile, b: IAudioFile) => {
            return b.priority - a.priority;
        });

        const numFiles = AudioPreloadManager.queue.length;

        for (let i = 0; i < numFiles; i++) {
            const file = this.queue[i];
            if (file.status === STATUS.NOT_LOADED) {
                if (file.priority >= MIN_PRIORITY_TO_LOAD) {
                    this.Load(file);
                    break;
                }
            }
        }
        for (let i = numFiles - 1; i > 0; i--) {
            const file = this.queue[i];
            if (AUTO_UNLOAD) {
                if (file.status === STATUS.LOADED) {
                    if (file.priority < MIN_PRIORITY_TO_LOAD) {
                        this.Unload(file);
                    }
                }
            }
        }
    }

    private static GetFileFromQueue(pFilename: string): IAudioFile | undefined {
        const numFiles = AudioPreloadManager.queue.length;
        for (let i = 0; i < numFiles; i++) {
            if (AudioPreloadManager.queue[i].filename === pFilename) {
                return AudioPreloadManager.queue[i];
            }
        }
        return undefined;
    }

    private static Load(pAudioFile: IAudioFile): void {
        if (pAudioFile.status === STATUS.NOT_LOADED) {
            pAudioFile.status = STATUS.LOADING;
            if (this.debug) {
                console.log("[AudioPreloadManager] loading "
                    + pAudioFile.filename + " (priority: " + pAudioFile.priority + ")");
            }
            Application.instance.audio.load(pAudioFile.filename, pAudioFile.category, () => {
                pAudioFile.status = STATUS.LOADED;
                if (this.debug) {
                    console.log("[AudioPreloadManager] loaded " + pAudioFile.filename);
                }
            });
        }
        else if (pAudioFile.status === STATUS.LOADED) {
            if (this.debug) {
                console.warn("[AudioPreloadManager] " + pAudioFile.filename + " is already loaded");
            }
        }
        else if (pAudioFile.status === STATUS.LOADING) {
            if (this.debug) {
                console.warn("[AudioPreloadManager] " + pAudioFile.filename + " is already loading");
            }
        }
    }
    private static Unload(pAudioFile: IAudioFile): void {
        if (pAudioFile.status === STATUS.LOADED) {
            pAudioFile.status = STATUS.NOT_LOADED;
            if (this.debug) {
                console.log("[AudioPreloadManager] unloading "
                    + pAudioFile.filename + " (priority: " + pAudioFile.priority + ")");
            }
            Application.instance.audio.unload(pAudioFile.filename, pAudioFile.category, false);
        }
        else if (pAudioFile.status === STATUS.NOT_LOADED) {
            if (this.debug) {
                console.warn("[AudioPreloadManager] " + pAudioFile.filename + " is already unloaded");
            }
        }
        else if (pAudioFile.status === STATUS.LOADING) {
            if (this.debug) {
                console.warn("[AudioPreloadManager] " + pAudioFile.filename + " is loading (can't unload)");
            }
        }
    }
}
